import { useState } from 'react';
import { Check, Copy, ExternalLink, Globe, Info, Link2Off } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import GalleryLinkToggle from './GalleryLinkToggle';
import ShareTabHeader from './ShareTabHeader';
import { shareableGalleryUrl, type GalleryLinkGate } from './shareGate';
import type { PublicLink } from './usePublicLink';
import { useGetGalleryBrandingQuery } from './services/shareApi';
import type { GalleryBranding } from '../../public-gallery/galleryBranding';

interface PhotosetGalleryTabProps {
  photosetId: string;
  link: PublicLink;
  gate: GalleryLinkGate;
  onDone: () => void;
}

/**
 * The Share modal's PlanOtour tab (DV-3714): the public link's switch
 * and, while it is on, the URL with Copy Link and Open, plus whose branding
 * the gallery shows. A live link on a hidden photoset offers only the switch,
 * to turn it off.
 */
const PhotosetGalleryTab = ({ photosetId, link, gate, onDone }: PhotosetGalleryTabProps) => {
  const liveUrl = shareableGalleryUrl(link.url, gate);
  const showOffNotice = link.url === null && gate.blockedBy === null;

  return (
    <div className="flex flex-col gap-4">
      <ShareTabHeader
        icon={Globe}
        title="Public link to this Photoset"
        audience="Public · No sign-in"
        tone="info"
      />
      {/* One card: the switch, and below a rule the link it controls. */}
      <div className="rounded-lg border border-border bg-background">
        <GalleryLinkToggle link={link} gate={gate} framed={false} className="p-4" />
        {liveUrl !== null && <LivePanel photosetId={photosetId} link={link} url={liveUrl} />}
      </div>
      {showOffNotice && (
        <div className="flex items-center gap-3 rounded-lg border border-dashed border-border p-3 text-sm text-muted-foreground">
          <Link2Off className="h-4 w-4 shrink-0" aria-hidden />
          <span>
            The link is off. Turning it on creates a new link; any link shared before stays off.
          </span>
        </div>
      )}
      <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-end">
        <Button type="button" onClick={onDone} className="w-full sm:w-auto">
          Done
        </Button>
      </div>
    </div>
  );
};

interface LivePanelProps {
  photosetId: string;
  link: PublicLink;
  url: string;
}

const LivePanel = ({ photosetId, link, url }: LivePanelProps) => {
  // Left out while loading or when the read fails: the line describes the
  // gallery, so it is never guessed.
  const { currentData: branding } = useGetGalleryBrandingQuery(photosetId);
  return (
    <div className="flex flex-col gap-3 border-t border-border p-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        {/* flex-1 only in the row: in the phone column a 0 basis would
            override h-10 and collapse the field to its text. */}
        <div className="flex h-10 min-w-0 items-center rounded-lg border border-input bg-background px-3 sm:flex-1">
          <Typography
            variant="body-sm"
            className="truncate font-mono"
            title={url}
            data-testid="gallery-link-url"
          >
            {url}
          </Typography>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={link.copy}
          disabled={link.busy}
          className={cn(
            'shrink-0 transition-colors',
            link.copied && 'bg-success-soft text-success-soft-foreground hover:bg-success-soft'
          )}
        >
          {link.copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {link.copied ? 'Copied' : 'Copy Link'}
        </Button>
        <Button asChild variant="outline" className="shrink-0">
          <a href={url} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-4 w-4" />
            Open
          </a>
        </Button>
      </div>
      {branding && <BrandingLine branding={branding} />}
    </div>
  );
};

/**
 * What the gallery's "presented by" shows, by the gallery header's own rule
 * (`galleryBranding`), including its fallback to the name when the logo fails
 * to load. Nothing when the gallery presents no one.
 */
const BrandingLine = ({ branding }: { branding: GalleryBranding }) => {
  const [logoFailed, setLogoFailed] = useState(false);
  const logoUrl = logoFailed ? null : branding.logoUrl;

  if (logoUrl !== null) {
    return (
      <div className="flex items-center gap-3">
        <img
          src={logoUrl}
          alt=""
          onError={() => setLogoFailed(true)}
          className="h-8 w-[52px] shrink-0 rounded-md border border-border bg-card object-contain p-1"
        />
        <Typography variant="body-sm" className="text-muted-foreground">
          PlanOtour is presented with the logo on file
          {branding.accountName !== null && (
            <>
              {' '}
              for <strong className="font-semibold text-foreground">{branding.accountName}</strong>
            </>
          )}
          .
        </Typography>
      </div>
    );
  }

  if (branding.accountName === null) return null;
  // A logo is on file, but the gallery cannot show it: not https, or it
  // failed to load.
  const logoUnusable = logoFailed || branding.logoRejected;
  return (
    <div className="flex items-center gap-3">
      <Info className="h-4 w-4 shrink-0 text-info" aria-hidden />
      <Typography variant="body-sm" className="text-muted-foreground">
        {logoUnusable ? 'The logo on file can’t be shown' : 'No logo is on file'}, so the PlanOtour
        shows “Presented by {branding.accountName}” as text. Email{' '}
        <a href="mailto:support@planomatic.com" className="text-primary underline">
          support@planomatic.com
        </a>{' '}
        to {logoUnusable ? 'replace it' : 'add one'}.
      </Typography>
    </div>
  );
};

export default PhotosetGalleryTab;
